import PropTypes from 'prop-types';
import React, {Component} from 'react';
import moment from 'moment';

import {
  chemicalBaseTypes,
  chemicalBaseTypeNames,
  systemTypeNames,
  truBlueStatus,
  countries,
  systemTypeNamesFormat
} from 'helpers/constants';
import {systemOptionsTags, addCommas} from 'helpers/dataHelpers';
import {
  namesAndIDs,
  truBlueSanitizers,
  truBlueMaintOxidizers,
  truBluePrevAlgicides,
  truBlueMaintClarifiers,
} from 'helpers/truBlue';
import getProductDict from '../system/productDict';
import changeLog from 'helpers/changeLog';

import {Col, Row} from 'components';
import {Select, Checkbox} from 'components/forms';
import {Select as ReadOnlySelect, Input as ReadOnlyInput} from 'components/forms/readOnly';


class TruBlueAssignSystem extends Component {

  static propTypes = {
    dealer: PropTypes.object,
    pool: PropTypes.object,
    products: PropTypes.array,
    register: PropTypes.func,
    unregister: PropTypes.func,
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
    onPoolSelectionChange: PropTypes.func,
  };

  constructor(props) {
    super(props);
    let {productDict, pool} = getProductDict({pool: props.pool, products: props.products, dealer: props.dealer, formatMessage: this.props.formatMessage});

    this.state = {
      checked: pool.isTruBlueActive,
      productDict,
      pool,
      backupPool: pool.clone(),
    };
  }

  onChange = ({key, value}) => {
    let pool = this.state.pool.clone();

    pool[key] = value;

    let maintClarifiers = truBlueMaintClarifiers(this.props.products, this.props.dealer, pool.chemicalBaseTypeID);
    let poolComplete = maintClarifiers[0];

    if (key === 'usingOptimizerPlus' && !value) { // Must select pool complete here
      pool.maintClarifier = poolComplete.id;
    }

    if (key === 'maintClarifier' && parseInt(value) !== poolComplete.id) {
      pool.usingOptimizerPlus = true;
    }

    let {productDict, pool: newPool} = getProductDict({
      pool,
      products: this.props.products,
      dealer: this.props.dealer,
      formatMessage: this.props.formatMessage,
    });

    this.setState({
      pool: newPool,
      productDict,
    });
  };

  onCheck = ({value}) => {
    let {pool} = this.state;
    let {dealer, products} = this.props;

    let originalPool = pool.clone();

    if (!value) {
      pool = this.state.backupPool.clone();
      if (pool.isTruBlueActive) {
        pool.truBlueExpiry = moment().subtract(1, 'day');
      }
    } else {
      pool.truBlue = pool.truBlue ? pool.truBlue : moment();
      pool.truBlueExpiry = moment().year(moment().year() + 1).month(0).date(5); // Expires jan 1st

      let sanitizers = truBlueSanitizers(products, dealer, pool.chemicalBaseTypeID);
      let maintOxidizers = truBlueMaintOxidizers(products, dealer, pool.chemicalBaseTypeID);
      let prevAlgicides = truBluePrevAlgicides(products, dealer, pool.chemicalBaseTypeID);
      let maintClarifiers = truBlueMaintClarifiers(products, dealer, pool.chemicalBaseTypeID);

      pool.sanitizer = sanitizers.find(x => x.id === pool.sanitizer) ? pool.sanitizer : dealer.truBlueSanitizer || sanitizers[0].id;
      pool.maintOxidizer = maintOxidizers.find(x => x.id === pool.maintOxidizer) ? pool.maintOxidizer : dealer.truBlueMaintOxidizer || maintOxidizers[0].id;
      pool.prevAlgicide = prevAlgicides.find(x => x.id === pool.prevAlgicide) ? pool.prevAlgicide : dealer.truBluePrevAlgicide || prevAlgicides[0].id;
      pool.maintClarifier = !pool.usingOptimizerPlus && dealer.truBluePoolComplete ? maintClarifiers[0].id : pool.maintClarifier;
      pool.usingOptimizerPlus = !dealer.truBlueOptimizerPlus && dealer.truBlueOptimizerPlus !== false ? pool.usingOptimizerPlus :
        pool.usingOptimizerPlus || dealer.truBlueOptimizerPlus;

      let changedFields = changeLog(['sanitizer', 'maintOxidizer', 'prevAlgicide', 'usingOptimizerPlus', 'maintClarifier'], originalPool, pool);

      if (changedFields.length > 0) {
        setTimeout(() => {
          for (let f of changedFields) {
            if (this[f]) {
              this[f].highlight();
            }
          }
        }, 200);
      }
    }

    let {pool: newPool, productDict} = getProductDict({
      pool,
      products: this.props.products,
      dealer: this.props.dealer,
      formatMessage: this.props.formatMessage,
    });

    this.setState({
      checked: value,
      pool: newPool,
      productDict,
    });

    this.props.onPoolSelectionChange(newPool, value);

  };

  render() {
    let {products, dealer, formatMessage, formatNumber} = this.props;
    let {checked, productDict, pool} = this.state;
    let options = systemOptionsTags[pool.typeID];

    const simpleFormat = id => formatMessage({id});

    let [sanitizerOptions, sanitizerKeys] = namesAndIDs(truBlueSanitizers(products, dealer, pool.chemicalBaseTypeID));
    let [maintOxidizerOptions, maintOxidizerKeys] = namesAndIDs(truBlueMaintOxidizers(products, dealer, pool.chemicalBaseTypeID));
    let [prevAlgicideOptions, prevAlgicideKeys] = namesAndIDs(truBluePrevAlgicides(products, dealer, pool.chemicalBaseTypeID));
    let [maintClarifierOptions, maintClarifierKeys] = namesAndIDs(truBlueMaintClarifiers(products, dealer, pool.chemicalBaseTypeID));

    let chemicalBaseTypeName = chemicalBaseTypeNames(pool.typeID, pool.chemicalBaseTypeID, formatMessage);
    let lastAnalysis = pool.prevAnalysisDate ? moment(pool.prevAnalysisDate).format('ll') : formatMessage({id: 'general.never'});
    let isPoolCurrentlyActive = pool.truBlueStatus === truBlueStatus.Active;

    return <div className='trublue-selection__system'>
      <h4 className='trublue-selection__system-title'>
        {!isPoolCurrentlyActive ?
          <Checkbox inline
                    dataChanged={this.onCheck}
                    value={this.state.checked}/> :
          null}
        {`${chemicalBaseTypeName} (${formatMessage({id: 'general.last.analysis'})}: ${lastAnalysis})`}
      </h4>
      <div className='trublue-selection__system-section'>
        <Row>
          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.system.type'}) + ':'}
                            options={Object.values(systemTypeNamesFormat(formatMessage))}
                            keys={Object.keys(systemTypeNamesFormat(formatMessage))}
                            value={pool.typeID + ''}/>
          </Col>

          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.ground.level'}) + ':'}
                            options={Object.values(options.groundLevel).map(simpleFormat)}
                            keys={Object.keys(options.groundLevel)}
                            value={pool.groundLevel}/>
          </Col>

          <Col md={3}>
            <ReadOnlyInput label={formatMessage({id: 'general.volume'}) + ':'}
                           value={formatNumber(pool.volume) + ' ' + (dealer.country === countries.CA ? 'L' : 'gal')}/>
          </Col>

          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.installation.type'}) + ':'}
                            options={Object.values(options.property).map(simpleFormat)}
                            keys={Object.keys(options.property)}
                            value={pool.property}/>
          </Col>

          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.location'}) + ':'}
                            options={Object.values(options.location).map(simpleFormat)}
                            keys={Object.keys(options.location)}
                            value={pool.location}/>
          </Col>

          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.filter.media'}) + ':'}
                            options={Object.values(options.filterMedia).map(simpleFormat)}
                            keys={Object.keys(options.filterMedia)}
                            value={pool.filterMedia}/>
          </Col>

          <Col md={3}>
            <ReadOnlySelect label={formatMessage({id: 'general.finish'}) + ':'}
                            options={Object.values(options.finish).map(simpleFormat)}
                            keys={Object.keys(options.finish)}
                            value={pool.finish}/>
          </Col>
        </Row>
      </div>
      <div className='trublue-selection__system-section'>
        <Row>
          <Col md={4}>
            {checked && !isPoolCurrentlyActive ? <Select label={formatMessage({id: 'general.sanitizer'}) + ':'}
                                                         required
                                                         options={sanitizerOptions}
                                                         keys={sanitizerKeys}
                                                         value={pool.sanitizer}
                                                         dataChanged={this.onChange}
                                                         dataKey='sanitizer'
                                                         ref={r => this.sanitizer = r}/> :
            <ReadOnlyInput label={formatMessage({id: 'general.sanitizer'}) + ':'}
                             value={productDict.sanitizer.currentName}/>}
          </Col>

          <Col md={4}>
            {checked && !isPoolCurrentlyActive ? <Select label={formatMessage({id: 'general.maint.oxidizer'}) + ':'}
                                                         required={pool.chemicalBaseTypeID !== chemicalBaseTypes.mineralSpringsPool}
                                                         options={maintOxidizerOptions}
                                                         keys={maintOxidizerKeys}
                                                         value={pool.maintOxidizer}
                                                         dataChanged={this.onChange}
                                                         dataKey='maintOxidizer'
                                                         ref={r => this.maintOxidizer = r}/> :
            <ReadOnlyInput label={formatMessage({id: 'general.maint.oxidizer'}) + ':'}
                             value={productDict.maintOxidizer.currentName}/>}
          </Col>

          <Col md={4}>
            {checked && !isPoolCurrentlyActive ? <Select label={formatMessage({id: 'general.prev.algicide'}) + ':'}
                                                         required={pool.chemicalBaseTypeID !== chemicalBaseTypes.mineralSpringsPool}
                                                         options={prevAlgicideOptions}
                                                         keys={prevAlgicideKeys}
                                                         value={pool.prevAlgicide}
                                                         dataChanged={this.onChange}
                                                         dataKey='prevAlgicide'
                                                         ref={r => this.prevAlgicide = r}/> :
            <ReadOnlyInput label={formatMessage({id: 'general.prev.algicide'}) + ':'}
                             value={productDict.prevAlgicide.currentName}/>}
          </Col>

          <Col md={4}>
            <Checkbox topLabel={formatMessage({id: 'general.water.enhancer'}) + ':'}
                      label='Optimizer Plus'
                      value={pool.usingOptimizerPlus}
                      editMode={!!checked}
                      dataChanged={this.onChange}
                      dataKey='usingOptimizerPlus'
                      disabled={pool.chemicalBaseTypeID === chemicalBaseTypes.mineralSpringsPool || isPoolCurrentlyActive} // Disable edit since it has to be always true for mineral springs pools confirmed with Andie.
                      ref={r => this.usingOptimizerPlus = r}/>
          </Col>

          <Col md={4}>
            {checked && !isPoolCurrentlyActive ? <Select label={formatMessage({id: 'general.maint.clarifier'}) + ':'}
                                                         required
                                                         options={pool.usingOptimizerPlus ? productDict.maintClarifier.names : maintClarifierOptions}
                                                         keys={pool.usingOptimizerPlus ? productDict.maintClarifier.keys : maintClarifierKeys}
                                                         value={pool.maintClarifier}
                                                         dataKey='maintClarifier'
                                                         dataChanged={this.onChange}
                                                         key='maintClarifier'
                                                         ref={r => this.maintClarifier = r}/> :
            <ReadOnlyInput label={formatMessage({id: 'general.maint.clarifier'}) + ':'}
                             value={productDict.maintClarifier.currentName}/>}
          </Col>
        </Row>
      </div>
    </div>;
  }
}

export default TruBlueAssignSystem;
